import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import StatCard from "components/ui/StatCard";
import Icon from "components/AppIcon";

import ProductTable from "./components/ProductTable";
import LoadingSkeleton from "./components/LoadingSkeleton";
import AddProductModal from "./components/AddProductModal";

import { useGetProducts } from "./useGetProducts";
import { useCreateProduct } from "./useCreateProduct";
import { useUpdateProduct } from "./useUpdateProduct";
import { useDeleteProduct } from "./useDeleteProduct";

import { avgUnitSellingPrice, countTaxable } from "./logic/stats";

const ProductManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");

  const { data: products = [], isLoading, isError, error } = useGetProducts();

  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  const deleteProductMutation = useDeleteProduct();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filtered = products.filter((product) => {
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();

    return (
      product.name?.toLowerCase().includes(term) ||
      product.description?.toLowerCase().includes(term) ||
      product.customsCode?.toLowerCase().includes(term) ||
      product.uniqueCode?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const taxableCount = countTaxable(products);
  const avgPrice = avgUnitSellingPrice(products);

  const stats = [
    {
      label: t("products.stats.total"),
      value: products.length,
      icon: "Package",
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
    },
    {
      label: t("products.stats.taxable"),
      value: taxableCount,
      icon: "Percent",
      color:
        "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
    },
    {
      label: t("products.stats.zeroRated"),
      value: products.length - taxableCount,
      icon: "Tag",
      color:
        "text-orange-500 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
    },
    {
      label: t("products.stats.avgUnitPrice"),
      value: `SR ${avgPrice.toLocaleString("en-SA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      icon: "TrendingUp",
      color: "text-primary bg-primary/10",
    },
  ];

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setIsProductModalOpen(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsProductModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingProduct(null);
    setIsProductModalOpen(false);
  };

  const handleSaveProduct = async (payload) => {
    try {
      if (payload.id) {
        await updateProductMutation.mutateAsync({
          id: payload.id,
          body: payload,
        });

        toast.success(t("products.successUpdate"));
      } else {
        await createProductMutation.mutateAsync(payload);
        toast.success(t("products.successCreate"));
      }

      handleCloseModal();
    } catch (error) {
      toast.error(error?.message || t("products.failedSave"));
    }
  };

  const handleDelete = async (productId) => {
    try {
      await deleteProductMutation.mutateAsync(productId);
      toast.success(t("products.successDelete"));
    } catch (error) {
      toast.error(error?.message || t("products.failedDelete"));
    }
  };

  const isSaving =
    createProductMutation.isPending || updateProductMutation.isPending;

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("products.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("products.description")}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                iconName="Download"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={() => toast(t("products.exportSoon"))}
              >
                {t("actions.export")}
              </Button>

              <Button
                variant="default"
                size="md"
                iconName="Plus"
                iconPosition="left"
                onClick={handleOpenCreate}
              >
                {t("actions.addProduct")}
              </Button>
            </div>
          </div>
        </motion.div>

        {isError && (
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
            <p className="text-sm text-red-700 dark:text-red-300">
              {error?.message || t("products.failedLoad")}
            </p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={20}
              className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />

            <input
              type="text"
              placeholder={t("products.searchPlaceholder")}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full ps-10 pe-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <ProductTable
            products={paginated}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isDeleting={deleteProductMutation.isPending}
          />
        </motion.div>

        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-6"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Package" size={32} className="text-primary" />
            </div>

            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("products.emptyTitle")}
            </h3>

            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? t("common.adjustSearchCriteria")
                : t("products.emptyDescription")}
            </p>

            {!searchTerm && (
              <Button
                iconName="Plus"
                iconPosition="left"
                onClick={handleOpenCreate}
              >
                {t("actions.addProduct")}
              </Button>
            )}
          </motion.div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t("common.showingRange", {
                  from: (currentPage - 1) * itemsPerPage + 1,
                  to: Math.min(currentPage * itemsPerPage, filtered.length),
                  total: filtered.length,
                  entity: t("entities.products"),
                })}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(page - 1, 1))
                  }
                />

                <span className="px-3 py-1 text-sm text-foreground">
                  {t("common.pageOf", { current: currentPage, total: totalPages })}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(page + 1, totalPages))
                  }
                />
              </div>
            </div>
          </div>
        )}

        <AnimatePresence>
          {isProductModalOpen && (
            <AddProductModal
              existingProduct={editingProduct}
              onSave={handleSaveProduct}
              onClose={handleCloseModal}
              isSaving={isSaving}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const ProductManagement = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <ProductManagementContent />
    </div>
  </SidebarProvider>
);

export default ProductManagement;
